import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, Pencil, Undo2, Redo2, Send, Sparkles, X, Smartphone,
  Share2, History, Keyboard, Check, QrCode, Clock,
} from "lucide-react";
import {
  FONT, T, RAIL_W, MAX_VERSIONS, DEVICES, SHARE_TTL_HOURS,
  MOBILE_TRAFFIC_THRESHOLD, originsFor, dayLabel, focusRing, prefersReducedMotion,
} from "./tokens";
import { useSectionBridge } from "./SectionBridge";
import PreviewFrame from "./PreviewFrame";
import DeviceToggle from "./DeviceToggle";
import DraftPill from "./DraftPill";
import VersionChip from "./VersionChip";
import HealthChip from "./HealthChip";
import CoachMarks from "./CoachMarks";
import CompareOverlay from "./CompareOverlay";

// ===========================================================================
// EditorLayout — the /website/edit split view. Left rail (scoped chat +
// versions + chat bar) · right live preview frame with the LIVEWIRE toolbar.
// Owns all editor state: staging vs published, versions, undo/redo, section
// selection, and every Part-4 extra (QR, share, health, keymap, coach marks).
// ===========================================================================

const DEMO_PATH = "/vella-demo-site.html";
const PREVIEW_SRC = `${DEMO_PATH}?vella_preview=1`;

const SHOWROOM_CSS = `
@keyframes showroomBurst{0%{transform:scale(.4);opacity:.9}100%{transform:scale(2.6);opacity:0}}
@keyframes slideInRight{from{opacity:0;transform:translateX(18px)}to{opacity:1;transform:translateX(0)}}
.showroom-scrollbar::-webkit-scrollbar{width:8px}
.showroom-scrollbar::-webkit-scrollbar-thumb{background:var(--slate-300);border-radius:8px}
@media (prefers-reduced-motion: reduce){
  #showroom-frame *,#showroom-frame{animation:none !important;transition:none !important}
}
`;

// --- Command → edit op translation (showroom stand-in for the LLM edit) -----
function parseCommand(text, section) {
  const raw = (text || "").trim();
  const lower = raw.toLowerCase();
  const quoted = raw.match(/["'“”‘’](.+?)["'“”‘’]/);
  const label = section?.label || "section";
  const wantsCta = /\b(cta|button|link)\b/.test(lower);
  const hexMatch = raw.match(/#[0-9a-fA-F]{3,6}\b/);
  const colorWord = lower.match(/\b(blue|teal|green|amber|gold|coral|rose|violet|navy|black|charcoal)\b/);

  if (quoted && wantsCta) return { op: { kind: "cta", text: quoted[1] }, summary: `changed ${label} button to "${quoted[1]}"` };
  if (quoted) return { op: { kind: "copy", text: quoted[1] }, summary: `rewrote ${label} copy` };
  if (/(bright|lighten|lighter|vivid|vibrant|pop|punch)/.test(lower)) return { op: { kind: "brighten" }, summary: `brightened ${label}` };
  if (/(bigger|larger|large|huge|bold|enlarge)/.test(lower)) return { op: { kind: "bigger" }, summary: `enlarged ${label} heading` };
  if (hexMatch || colorWord) {
    const map = { blue: "#275c73", teal: "#2f8f9d", green: "#3f8a63", amber: "#c89243", gold: "#c89243", coral: "#e0715e", rose: "#c95c82", violet: "#7c5cc9", navy: "#1f3e4c", black: "#16232a", charcoal: "#2a3137" };
    const value = hexMatch ? hexMatch[0] : map[colorWord[1]] || "#275c73";
    return { op: { kind: "accent", value }, summary: `recolored ${label} accent` };
  }
  return { op: { kind: "tweak" }, summary: `updated ${label}` };
}

function useReducedMotion() {
  const [rm, setRm] = useState(prefersReducedMotion());
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch {
      return;
    }
    const on = () => setRm(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.addEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  return rm;
}

let VERSION_SEQ = 0;
let EDIT_SEQ = 0;

export default function EditorLayout({ biz, onExit, analytics }) {
  const iframeRef = useRef(null);
  const reduce = useReducedMotion();

  // --- Preview / device --------------------------------------------------
  const mobilePct = Number(analytics?.mobilePct ?? biz?.analytics?.mobilePct ?? 0);
  const [device, setDevice] = useState("desktop");
  const [smartTip, setSmartTip] = useState(false);
  const [loading, setLoading] = useState(true);

  // --- Editing state -----------------------------------------------------
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(null); // {id,label}
  const [hovered, setHovered] = useState(null);

  const [published, setPublished] = useState([]); // live baseline (edit[])
  const [working, setWorking] = useState([]); // currently-applied edit[]
  const workingRef = useRef(working);
  workingRef.current = working;
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const [versions, setVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState(null);
  const [compare, setCompare] = useState(null); // version being compared

  const [publishing, setPublishing] = useState(false);
  const [burst, setBurst] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [historyLog, setHistoryLog] = useState([]);
  const [drawer, setDrawer] = useState(null); // 'history' | 'shortcuts' | 'share' | 'qr' | null
  const [fixedChecks, setFixedChecks] = useState({});

  const [toast, setToastRaw] = useState(null);
  const toastTimer = useRef(null);
  const toastMsg = useCallback((msg) => {
    setToastRaw({ msg, at: Date.now() });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastRaw(null), 2600);
  }, []);

  // --- Scoped chat -------------------------------------------------------
  const siteName = biz?.name || "Harbor & Vine";
  const liveHost = (biz?.website || "harborandvine.com").replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  const [chat, setChat] = useState(() => [
    { id: "m0", role: "vella", text: `This is your live site. Turn on Edit and click a section, then tell me what to change — I'll only touch that part.` },
  ]);
  const [input, setInput] = useState("");
  const chatScrollRef = useRef(null);

  const allowlist = useMemo(() => originsFor(PREVIEW_SRC), []);

  // --- Bridge ------------------------------------------------------------
  const rebuild = useCallback((edits) => {
    // Full deterministic re-apply: revert to originals, replay edit list.
    bridgeRef.current.revertAll();
    (edits || []).forEach((e) => bridgeRef.current.applyEdit(e.section, e.op));
  }, []);

  const bridge = useSectionBridge({
    iframeRef,
    allowlist,
    onSectionClick: (id) => {
      const s = sectionsRef.current.find((x) => x.id === id);
      const next = { id, label: s?.label || id };
      setSelected(next);
      if (!editMode) setEditMode(true);
    },
    onSectionHover: (id) => setHovered(id),
    onReady: () => {
      // Re-apply the working set whenever the bridge (re)connects.
      rebuild(workingRef.current);
    },
  });
  const bridgeRef = useRef(bridge);
  bridgeRef.current = bridge;
  const sectionsRef = useRef([]);
  sectionsRef.current = bridge.sections;

  // Push edit-mode into the iframe whenever it toggles or reconnects.
  useEffect(() => {
    bridge.setEditMode(editMode);
    if (!editMode) setSelected(null);
  }, [editMode, bridge.ready]);

  // Highlight the selected/hovered section inside the frame.
  useEffect(() => {
    bridge.highlight(selected?.id || null);
  }, [selected]);

  // Smart device default (Part 4.4).
  useEffect(() => {
    if (mobilePct > MOBILE_TRAFFIC_THRESHOLD) {
      setDevice("mobile");
      setSmartTip(true);
      const t = setTimeout(() => setSmartTip(false), 6000);
      return () => clearTimeout(t);
    }
  }, [mobilePct]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat]);

  // --- Staged diff -------------------------------------------------------
  const publishedIds = useMemo(() => new Set(published.map((e) => e.id)), [published]);
  const stagedEdits = useMemo(() => working.filter((e) => !publishedIds.has(e.id)), [working, publishedIds]);
  const stagedSections = useMemo(() => new Set(stagedEdits.map((e) => e.sectionLabel)), [stagedEdits]);

  // --- Edit operations ---------------------------------------------------
  const addEdit = useCallback(
    (op, summary, section) => {
      const edit = {
        id: `e${++EDIT_SEQ}`,
        section: section.id,
        sectionLabel: section.label,
        op,
        summary,
        ts: Date.now(),
      };
      setWorking((w) => {
        const next = [...w, edit];
        return next;
      });
      bridgeRef.current.applyEdit(edit.section, edit.op);
      bridgeRef.current.flash(edit.section);
      setUndoStack((s) => [...s, edit]);
      setRedoStack([]);
      setActiveVersion(null);
      setHistoryLog((h) => [...h, edit]);
      return edit;
    },
    []
  );

  const undo = useCallback(() => {
    setUndoStack((s) => {
      if (!s.length) return s;
      const last = s[s.length - 1];
      const nextWorking = workingRef.current.filter((e) => e.id !== last.id);
      setWorking(nextWorking);
      rebuild(nextWorking);
      setRedoStack((r) => [...r, last]);
      toastMsg(`Undid: ${last.summary}`);
      return s.slice(0, -1);
    });
  }, [rebuild, toastMsg]);

  const redo = useCallback(() => {
    setRedoStack((r) => {
      if (!r.length) return r;
      const last = r[r.length - 1];
      const nextWorking = [...workingRef.current, last];
      setWorking(nextWorking);
      rebuild(nextWorking);
      setUndoStack((s) => [...s, last]);
      toastMsg(`Redid: ${last.summary}`);
      return r.slice(0, -1);
    });
  }, [rebuild, toastMsg]);

  const discard = useCallback(() => {
    setWorking(published.slice());
    rebuild(published);
    setUndoStack([]);
    setRedoStack([]);
    setActiveVersion(null);
    toastMsg("Reverted to the published site");
  }, [published, rebuild, toastMsg]);

  const doPublish = useCallback(() => {
    setConfirmOpen(false);
    setPublishing(true);
    const snapshot = workingRef.current.slice();
    const commit = () => {
      const label = `v${++VERSION_SEQ}`;
      setPublished(snapshot);
      setVersions((v) => {
        const note = snapshot.length ? snapshot[snapshot.length - 1].summary : "initial";
        const next = [...v, { label, note, ts: Date.now(), edits: snapshot }];
        return next.slice(-MAX_VERSIONS); // FIFO cap
      });
      setActiveVersion(label);
      setUndoStack([]);
      setRedoStack([]);
      setPublishing(false);
      if (!reduce) {
        setBurst(true);
        setTimeout(() => setBurst(false), 720);
      }
      toastMsg("Published — your changes are live");
    };
    // brief "publishing" beat for believability
    if (reduce) commit();
    else setTimeout(commit, 520);
  }, [reduce, toastMsg]);

  const restoreVersion = useCallback(
    (version) => {
      setWorking(version.edits.slice());
      rebuild(version.edits);
      setUndoStack([]);
      setRedoStack([]);
      setActiveVersion(version.label);
      toastMsg(`Restored ${version.label} to staging`);
    },
    [rebuild, toastMsg]
  );

  // --- Chat send ---------------------------------------------------------
  const send = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setChat((c) => [...c, { id: `u${Date.now()}`, role: "user", text, scope: selected?.label || null }]);
    setInput("");
    if (selected) {
      const { op, summary } = parseCommand(text, selected);
      addEdit(op, summary, selected);
      setChat((c) => [...c, { id: `v${Date.now()}`, role: "vella", text: `Done — I ${summary}. It's staged, so it won't go live until you publish.` }]);
    } else {
      setChat((c) => [...c, { id: `v${Date.now()}`, role: "vella", text: `Turn on Edit and click a section first, then I can apply that to just that part of the page.` }]);
    }
  }, [input, selected, addEdit]);

  // --- SEO / health checks (Part 4.3) ------------------------------------
  const checks = useMemo(
    () => [
      { id: "title", label: "Page title", ok: true },
      { id: "meta", label: "Meta description", ok: true },
      { id: "mobile", label: "Mobile-friendly", ok: true },
      { id: "schema", label: "Structured data (schema)", ok: !!fixedChecks.schema, hint: "No LocalBusiness schema found." },
      { id: "alts", label: "Image alt text", ok: !!fixedChecks.alts, hint: "2 gallery images are missing alt text." },
    ],
    [fixedChecks]
  );
  const fixCheck = useCallback(
    (check) => {
      setFixedChecks((f) => ({ ...f, [check.id]: true }));
      const summary = check.id === "schema" ? "added LocalBusiness schema" : "added alt text to gallery images";
      setChat((c) => [
        ...c,
        { id: `uq${Date.now()}`, role: "user", text: `Fix: ${check.label}` },
        { id: `vq${Date.now()}`, role: "vella", text: `Done — I ${summary}. Publish when you're ready.` },
      ]);
      setHistoryLog((h) => [...h, { id: `fix${++EDIT_SEQ}`, sectionLabel: "SEO", summary, ts: Date.now() }]);
      toastMsg(`Vella ${summary}`);
    },
    [toastMsg]
  );

  // --- Keyboard map (Part 4.5) -------------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || "") || e.target?.isContentEditable;
      const meta = e.metaKey || e.ctrlKey;
      // cmd/ctrl+enter → publish (with confirm)
      if (meta && e.key === "Enter") {
        e.preventDefault();
        if (stagedEdits.length) setConfirmOpen(true);
        return;
      }
      if (meta && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
        return;
      }
      if (e.key === "Escape") {
        if (compare) return; // overlay handles its own esc
        if (confirmOpen) setConfirmOpen(false);
        else if (drawer) setDrawer(null);
        else if (selected) setSelected(null);
        else if (editMode) setEditMode(false);
        return;
      }
      if (typing || meta) return;
      if (e.key === "e" || e.key === "E") { setEditMode((m) => !m); return; }
      if (e.key === "?") { setDrawer((d) => (d === "shortcuts" ? null : "shortcuts")); return; }
      const dev = DEVICES.find((d) => d.key === e.key);
      if (dev) { setDevice(dev.id); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo, stagedEdits.length, selected, editMode, drawer, confirmOpen, compare]);

  // --- Share link (Part 4.2) ---------------------------------------------
  const shareLink = useMemo(() => {
    const token = Math.abs((Date.now() ^ (EDIT_SEQ + 7) * 2654435761) >>> 0).toString(36) + Math.abs((VERSION_SEQ + 13) * 40503).toString(36);
    return `${liveHost}/preview/${token}`;
  }, [liveHost]);
  const shareExpiry = useMemo(() => dayLabel(Date.now() + SHARE_TTL_HOURS * 3600 * 1000), []);
  const liveUrlFull = `https://${liveHost}`;

  // --- Render ------------------------------------------------------------
  const railHeaderNote = editMode ? "Click a section to edit it" : "Editing your live site";

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", flexDirection: "column", background: "var(--surface-app)", fontFamily: FONT, color: T.ink }}>
      <style>{SHOWROOM_CSS}</style>

      {/* ── Editor top bar ────────────────────────────────────────── */}
      <header
        style={{
          flexShrink: 0, height: 56, display: "flex", alignItems: "center", gap: 12, padding: "0 16px",
          background: "rgba(255,255,255,0.72)", backdropFilter: "blur(30px) saturate(180%)", WebkitBackdropFilter: "blur(30px) saturate(180%)",
          borderBottom: `1px solid ${T.line}`, boxShadow: T.shadowSm,
        }}
      >
        <button
          type="button" onClick={onExit} aria-label="Back to Vella"
          onFocus={(e) => (e.currentTarget.style.boxShadow = focusRing.boxShadow)} onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
          style={{ display: "inline-flex", alignItems: "center", gap: 7, height: 32, padding: "0 12px", borderRadius: T.radiusFull, border: `1px solid ${T.line}`, background: "rgba(255,255,255,0.7)", color: T.ink2, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          <ArrowLeft size={15} strokeWidth={2} aria-hidden="true" /> Back
        </button>
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.15, minWidth: 0 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{siteName}</span>
          <span style={{ fontSize: 11.5, color: T.ink3 }}>{liveHost}</span>
        </div>

        {/* center — draft state */}
        <div id="showroom-publish-anchor" style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <DraftPill
            count={stagedEdits.length}
            sectionCount={stagedSections.size}
            publishing={publishing}
            onPublish={() => setConfirmOpen(true)}
            onDiscard={discard}
          />
        </div>

        {/* right — utilities */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <IconBtn label="View on your phone" onClick={() => setDrawer((d) => (d === "qr" ? null : "qr"))} active={drawer === "qr"}><QrCode size={16} strokeWidth={1.9} /></IconBtn>
          <IconBtn label="Share a preview link" onClick={() => setDrawer((d) => (d === "share" ? null : "share"))} active={drawer === "share"}><Share2 size={16} strokeWidth={1.9} /></IconBtn>
          <IconBtn label="Edit history" onClick={() => setDrawer((d) => (d === "history" ? null : "history"))} active={drawer === "history"}><History size={16} strokeWidth={1.9} /></IconBtn>
          <IconBtn label="Keyboard shortcuts" onClick={() => setDrawer((d) => (d === "shortcuts" ? null : "shortcuts"))} active={drawer === "shortcuts"}><Keyboard size={16} strokeWidth={1.9} /></IconBtn>
        </div>
      </header>

      {/* ── Body: rail + preview ──────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* Left rail */}
        <aside
          style={{
            width: RAIL_W, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0,
            borderRight: `1px solid ${T.line}`, background: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(24px) saturate(160%)", WebkitBackdropFilter: "blur(24px) saturate(160%)",
          }}
        >
          {/* rail header */}
          <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${T.line}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Vella · site editor</div>
            <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2 }}>{railHeaderNote}</div>
            {versions.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                {versions.map((v) => (
                  <VersionChip key={v.label} version={v} active={activeVersion === v.label} onRestore={restoreVersion} onCompareStart={(ver) => setCompare(ver)} />
                ))}
              </div>
            )}
          </div>

          {/* chat scroll */}
          <div ref={chatScrollRef} className="showroom-scrollbar" style={{ flex: 1, minHeight: 0, overflow: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            {chat.map((m) => (
              <ChatBubble key={m.id} m={m} />
            ))}
          </div>

          {/* chat bar (bottom of rail) */}
          <div style={{ flexShrink: 0, padding: 12, borderTop: `1px solid ${T.line}` }}>
            {selected && (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 8, padding: "4px 6px 4px 10px", borderRadius: T.radiusFull, background: "rgba(62,198,255,0.14)", border: "1px solid rgba(62,198,255,0.45)", fontSize: 12, fontWeight: 600, color: "#0b5566" }}>
                <span>Editing: {selected.label}</span>
                <button type="button" aria-label="Clear section scope" onClick={() => setSelected(null)} style={{ width: 18, height: 18, borderRadius: T.radiusFull, border: "none", background: "rgba(11,85,102,0.12)", color: "#0b5566", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={11} strokeWidth={2.4} />
                </button>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: 8, borderRadius: T.radiusMd, background: "#fff", border: `1px solid ${T.line}`, boxShadow: T.shadowSm }}>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1}
                placeholder={selected ? `Tell Vella how to change the ${selected.label.toLowerCase()}…` : "Ask Vella to change your site…"}
                aria-label="Message Vella"
                style={{ flex: 1, resize: "none", border: "none", outline: "none", background: "transparent", fontFamily: FONT, fontSize: 13.5, lineHeight: 1.5, color: T.ink, maxHeight: 120 }}
              />
              <button
                type="button" onClick={send} aria-label="Send"
                onFocus={(e) => (e.currentTarget.style.boxShadow = focusRing.boxShadow)} onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                style={{ width: 34, height: 34, flexShrink: 0, borderRadius: T.radiusMd, border: "none", background: input.trim() ? T.blue : T.line2, color: "#fff", cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: `background var(--ds-dur-fast) ${T.easeOut}` }}
              >
                <Send size={15} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
          </div>
        </aside>

        {/* Preview region */}
        <main style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", position: "relative" }}>
          <PreviewFrame
            iframeRef={iframeRef}
            src={PREVIEW_SRC}
            device={device}
            displayUrl={liveHost}
            loading={loading}
            onLoad={() => setLoading(false)}
            onReload={() => {
              setLoading(true);
              if (iframeRef.current) iframeRef.current.src = PREVIEW_SRC; // re-inits bridge → onReady replays edits
            }}
            burst={burst}
            headerRight={<HealthChip checks={checks} onFix={fixCheck} />}
            toolbar={
              <LivewireToolbar
                editMode={editMode}
                onToggleEdit={() => setEditMode((m) => !m)}
                canUndo={undoStack.length > 0}
                canRedo={redoStack.length > 0}
                onUndo={undo}
                onRedo={redo}
                device={device}
                onDevice={setDevice}
              />
            }
          />

          {/* smart-default tooltip (Part 4.4) */}
          {smartTip && (
            <div role="status" style={{ position: "absolute", bottom: 18, left: "50%", transform: "translateX(-50%)", zIndex: 35, display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: T.radiusFull, background: "rgba(15,23,42,0.9)", color: "#fff", fontSize: 12.5, fontWeight: 500, boxShadow: T.shadowLg }}>
              <Smartphone size={14} strokeWidth={2} aria-hidden="true" />
              Most of your visitors are on mobile — starting in Phone view.
              <button type="button" onClick={() => setSmartTip(false)} aria-label="Dismiss" style={{ border: "none", background: "transparent", color: "#9fd4ff", cursor: "pointer", fontWeight: 700 }}>Got it</button>
            </div>
          )}
        </main>

        {/* right-docked drawers */}
        {drawer === "history" && <HistoryDrawer log={historyLog} onClose={() => setDrawer(null)} />}
        {drawer === "shortcuts" && <ShortcutSheet onClose={() => setDrawer(null)} />}
        {drawer === "share" && <SharePopover link={shareLink} expiry={shareExpiry} onCopy={() => toastMsg("Preview link copied")} onClose={() => setDrawer(null)} />}
        {drawer === "qr" && <QRPopover url={liveUrlFull} host={liveHost} onClose={() => setDrawer(null)} />}
      </div>

      {/* Publish confirm (Part 2.4) */}
      {confirmOpen && (
        <PublishConfirm edits={stagedEdits} onCancel={() => setConfirmOpen(false)} onConfirm={doPublish} />
      )}

      {/* Before/after compare (Part 2.5) */}
      {compare && (
        <CompareOverlay
          demoUrl={PREVIEW_SRC}
          beforeEdits={working}
          afterEdits={compare.edits}
          beforeLabel="Current"
          afterLabel={compare.label}
          onClose={() => setCompare(null)}
        />
      )}

      {/* Coach marks (Part 2.8) */}
      <CoachMarks />

      {/* Toast */}
      {toast && (
        <div role="status" aria-live="polite" style={{ position: "fixed", bottom: 22, left: `calc(${RAIL_W / 2}px)`, transform: "translateX(-50%)", zIndex: 1400, background: "#0f2932", color: "#fff", borderRadius: T.radiusMd, padding: "9px 15px", fontSize: 13, fontWeight: 500, boxShadow: T.shadowLg, maxWidth: 320 }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ── Small building blocks ───────────────────────────────────────────────

function IconBtn({ label, onClick, active, children }) {
  return (
    <button
      type="button" onClick={onClick} aria-label={label} title={label} aria-pressed={!!active}
      onFocus={(e) => (e.currentTarget.style.boxShadow = focusRing.boxShadow)} onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
      style={{ width: 34, height: 34, borderRadius: T.radiusFull, border: `1px solid ${active ? T.blue : T.line}`, background: active ? "rgba(39,92,115,0.08)" : "rgba(255,255,255,0.7)", color: active ? T.blue : T.ink2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      {children}
    </button>
  );
}

function ChatBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start", gap: 4 }}>
      {m.scope && <span style={{ fontSize: 10.5, fontWeight: 700, color: "#0b5566", background: "rgba(62,198,255,0.14)", padding: "2px 8px", borderRadius: T.radiusFull }}>{m.scope}</span>}
      <div
        style={{
          maxWidth: "86%", padding: "9px 12px", borderRadius: T.radiusMd, fontSize: 13, lineHeight: 1.5,
          background: isUser ? T.blue : "#fff", color: isUser ? "#fff" : T.ink,
          border: isUser ? "none" : `1px solid ${T.line}`,
          borderBottomRightRadius: isUser ? 4 : T.radiusMd, borderBottomLeftRadius: isUser ? T.radiusMd : 4,
        }}
      >
        {m.text}
      </div>
    </div>
  );
}

function LivewireToolbar({ editMode, onToggleEdit, canUndo, canRedo, onUndo, onRedo, device, onDevice }) {
  return (
    <div
      role="toolbar" aria-label="Livewire editing toolbar"
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: 6, borderRadius: T.radiusFull, background: "rgba(255,255,255,0.86)", backdropFilter: "blur(20px) saturate(180%)", WebkitBackdropFilter: "blur(20px) saturate(180%)", border: `1px solid ${T.line}`, boxShadow: T.shadowLg }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, paddingLeft: 6, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: T.ink3 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: editMode ? "#3EC6FF" : T.line2 }} />
        LIVEWIRE
      </span>
      <span style={{ width: 1, height: 20, background: T.line }} />
      <button
        id="showroom-edit-toggle"
        type="button" onClick={onToggleEdit} aria-pressed={editMode} title="Toggle edit mode (E)"
        onFocus={(e) => (e.currentTarget.style.boxShadow = focusRing.boxShadow)} onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 14px", borderRadius: T.radiusFull, border: `1px solid ${editMode ? T.blue : T.line}`, background: editMode ? T.blue : "transparent", color: editMode ? "#fff" : T.ink2, fontFamily: FONT, fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}
      >
        <Pencil size={13} strokeWidth={2} aria-hidden="true" /> Edit
      </button>
      <ToolIcon label="Undo (⌘Z)" disabled={!canUndo} onClick={onUndo}><Undo2 size={15} strokeWidth={2} /></ToolIcon>
      <ToolIcon label="Redo (⌘⇧Z)" disabled={!canRedo} onClick={onRedo}><Redo2 size={15} strokeWidth={2} /></ToolIcon>
      <span style={{ width: 1, height: 20, background: T.line }} />
      <DeviceToggle value={device} onChange={onDevice} />
    </div>
  );
}

function ToolIcon({ label, disabled, onClick, children }) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label}
      onFocus={(e) => !disabled && (e.currentTarget.style.boxShadow = focusRing.boxShadow)} onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
      style={{ width: 32, height: 30, borderRadius: T.radiusFull, border: "none", background: "transparent", color: disabled ? T.line2 : T.ink2, cursor: disabled ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      {children}
    </button>
  );
}

// ── Dialogs / drawers ───────────────────────────────────────────────────

function PublishConfirm({ edits, onConfirm, onCancel }) {
  const reduce = prefersReducedMotion();
  return (
    <div role="dialog" aria-modal="true" aria-label="Publish changes" style={{ position: "fixed", inset: 0, zIndex: 1500, background: "rgba(15,23,42,0.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: FONT }} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: "100%", background: T.surface, borderRadius: T.radiusLg, border: `1px solid ${T.line}`, boxShadow: T.shadowLg, padding: 22, animation: reduce ? "none" : "slideUp 0.24s ease" }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Publish {edits.length} change{edits.length === 1 ? "" : "s"}?</div>
        <div style={{ fontSize: 13, color: T.ink3, marginBottom: 14 }}>These go live at your domain immediately. You can roll back anytime.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflow: "auto", marginBottom: 18 }}>
          {edits.length === 0 && <div style={{ fontSize: 13, color: T.ink3 }}>No staged changes.</div>}
          {edits.map((e) => (
            <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", borderRadius: T.radiusSm, background: "rgba(39,92,115,0.05)" }}>
              <Check size={14} strokeWidth={2.4} style={{ color: T.green, flexShrink: 0 }} aria-hidden="true" />
              <span style={{ fontSize: 12.5, color: T.ink }}>
                <b style={{ fontWeight: 700 }}>{e.sectionLabel}</b> — {e.summary.replace(new RegExp(`^\\w+ ${e.sectionLabel.toLowerCase()} ?`, "i"), "").trim() || e.summary}
              </span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={onCancel} style={{ height: 36, padding: "0 16px", borderRadius: T.radiusFull, border: `1px solid ${T.line}`, background: "rgba(255,255,255,0.7)", color: T.ink2, fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
          <button type="button" onClick={onConfirm} autoFocus style={{ height: 36, padding: "0 20px", borderRadius: T.radiusFull, border: `1px solid ${T.blue}`, background: T.blue, color: "#fff", fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: T.shadowSm }}>Publish now</button>
        </div>
      </div>
    </div>
  );
}

function Drawer({ title, onClose, children }) {
  const reduce = prefersReducedMotion();
  return (
    <aside
      aria-label={title}
      style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 320, zIndex: 60, background: T.surface, borderLeft: `1px solid ${T.line}`, boxShadow: "-12px 0 40px -20px rgba(15,23,42,0.3)", display: "flex", flexDirection: "column", animation: reduce ? "none" : "slideInRight 0.24s ease", fontFamily: FONT }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: `1px solid ${T.line}` }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{title}</span>
        <button type="button" onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, borderRadius: T.radiusFull, border: `1px solid ${T.line}`, background: "rgba(255,255,255,0.7)", color: T.ink2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={14} strokeWidth={2} />
        </button>
      </div>
      <div className="showroom-scrollbar" style={{ flex: 1, overflow: "auto", padding: 16 }}>{children}</div>
    </aside>
  );
}

function HistoryDrawer({ log, onClose }) {
  // Group by day, newest first.
  const groups = useMemo(() => {
    const by = new Map();
    log.slice().reverse().forEach((e) => {
      const key = dayLabel(e.ts);
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(e);
    });
    return Array.from(by.entries());
  }, [log]);
  return (
    <Drawer title="Edit history" onClose={onClose}>
      {log.length === 0 && <div style={{ fontSize: 13, color: T.ink3 }}>No edits yet. Changes you make will show up here, grouped by day.</div>}
      {groups.map(([day, items]) => (
        <div key={day} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.ink3, marginBottom: 8 }}>{day}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map((e) => (
              <div key={e.id} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                <Clock size={13} strokeWidth={2} style={{ color: T.ink3, marginTop: 2, flexShrink: 0 }} aria-hidden="true" />
                <div style={{ fontSize: 12.5, color: T.ink, lineHeight: 1.45 }}>
                  <b style={{ fontWeight: 700 }}>{e.sectionLabel}</b> — {e.summary}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </Drawer>
  );
}

function ShortcutSheet({ onClose }) {
  const rows = [
    ["E", "Toggle edit mode"],
    ["1 / 2 / 3", "Desktop / Tablet / Phone"],
    ["⌘ / Ctrl + Z", "Undo staged edit"],
    ["⌘ / Ctrl + ⇧ + Z", "Redo"],
    ["⌘ / Ctrl + Enter", "Publish (with confirm)"],
    ["Esc", "Exit selection / edit mode"],
    ["?", "This shortcut sheet"],
  ];
  return (
    <Drawer title="Keyboard shortcuts" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map(([k, d]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span style={{ fontSize: 12.5, color: T.ink2 }}>{d}</span>
            <kbd style={{ fontFamily: "var(--ds-font-mono)", fontSize: 11.5, fontWeight: 600, color: T.ink, background: "rgba(39,92,115,0.06)", border: `1px solid ${T.line}`, borderRadius: T.radiusSm, padding: "3px 8px", whiteSpace: "nowrap" }}>{k}</kbd>
          </div>
        ))}
      </div>
    </Drawer>
  );
}

function SharePopover({ link, expiry, onCopy, onClose }) {
  const copy = () => {
    try {
      navigator.clipboard && navigator.clipboard.writeText(`https://${link}`);
    } catch {
      /* ignore */
    }
    onCopy && onCopy();
  };
  return (
    <Drawer title="Share a preview" onClose={onClose}>
      <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5, marginBottom: 14 }}>
        A read-only link to your <b>staged draft</b>. Anyone with it can view — not edit — and it expires in {SHARE_TTL_HOURS} hours (around {expiry}).
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 10, borderRadius: T.radiusMd, background: "rgba(39,92,115,0.05)", border: `1px solid ${T.line}`, marginBottom: 12 }}>
        <span style={{ flex: 1, minWidth: 0, fontFamily: "var(--ds-font-mono)", fontSize: 12, color: T.ink2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{link}</span>
      </div>
      <button type="button" onClick={copy} style={{ width: "100%", height: 38, borderRadius: T.radiusFull, border: `1px solid ${T.blue}`, background: T.blue, color: "#fff", fontFamily: FONT, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: T.shadowSm }}>Copy preview link</button>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 11.5, color: T.ink3 }}>
        <Clock size={12} strokeWidth={2} aria-hidden="true" /> Token is single-use and unguessable.
      </div>
    </Drawer>
  );
}

function QRPopover({ url, host, onClose }) {
  const [failed, setFailed] = useState(false);
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=0&data=${encodeURIComponent(url)}`;
  return (
    <Drawer title="View on your phone" onClose={onClose}>
      <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5, marginBottom: 14 }}>Scan with your phone camera to open the live site.</div>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
        <div style={{ width: 200, height: 200, borderRadius: T.radiusMd, border: `1px solid ${T.line}`, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: 10 }}>
          {failed ? (
            <span style={{ fontSize: 12, color: T.ink3, textAlign: "center", padding: 12 }}>Couldn't load a QR code offline. Open<br /><b>{host}</b><br />on your phone.</span>
          ) : (
            <img src={qrSrc} alt={`QR code linking to ${host}`} width={180} height={180} onError={() => setFailed(true)} style={{ display: "block" }} />
          )}
        </div>
      </div>
      <div style={{ textAlign: "center", fontFamily: "var(--ds-font-mono)", fontSize: 12.5, color: T.ink2 }}>{host}</div>
    </Drawer>
  );
}
